import type { AppRole, AppWorkLine } from "@cdc-sccr/shared";

interface AuthState {
  token: string;
  csrfToken: string;
  user: {
    id: string
    email: string
    firstName: string
    lastName: string
    role: AppRole
    workLines?: AppWorkLine[] | null
    isSuperAdmin?: true
  };
}

let _auth: AuthState | null = null;

/** Decodifica el payload del JWT (sin verificar la firma) */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    // base64url → standard base64
    const payload = parts[1] ?? "";
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

/**
 * Intenta restaurar la sesión desde localStorage.
 * Llama a esta función UNA vez antes de montar la app (main.tsx).
 */
export function initAuth(): void {
  const token = localStorage.getItem("token");
  const userRaw = localStorage.getItem("user");
  if (!token || !userRaw) return;

  const payload = decodeJwtPayload(token);
  if (!payload) {
    clearAuth();
    return;
  }

  // Check expiration
  const exp = typeof payload.exp === "number" ? payload.exp : 0;
  if (Date.now() / 1000 > exp) {
    clearAuth();
    return;
  }

  try {
    const user = JSON.parse(userRaw) as AuthState["user"];
    // csrfToken = sessionId que viene dentro del JWT
    const csrfToken = typeof payload.sessionId === "string" ? payload.sessionId : "";
    _auth = { token, csrfToken, user };
  } catch {
    clearAuth();
  }
}

export function getAuth(): AuthState | null {
  return _auth;
}

export function setAuth(state: AuthState): void {
  _auth = state;
  localStorage.setItem("token", state.token);
  // Guardamos info del usuario (no el csrfToken, que viaja solo en memoria)
  localStorage.setItem("user", JSON.stringify(state.user));
}

export function clearAuth(): void {
  _auth = null;
  localStorage.removeItem("token");
  localStorage.removeItem("user");
}

export function isAuthenticated(): boolean {
  return _auth !== null;
}
